/**
 * What this flow map looked like before.
 *
 * Every save writes the version it replaced, and the last fifty are kept. This
 * is the only way to read them back: pick a version, look at it as a real map
 * rather than as a diff, and put it back if it was better.
 *
 * Restoring is an ordinary save. It writes the current map into the history
 * first, so undoing a restore is one more click rather than a lost afternoon,
 * and it goes through the same version check as any other save: if somebody
 * else saved while this dialog was open, the restore meets the usual conflict
 * banner instead of quietly winning.
 *
 * A version with no author was written by the seed on API boot, which is the
 * handbook's own starting map. It is named as that rather than left blank,
 * because a blank author reads as "we lost the name".
 */
import { useEffect, useState } from "react";
import { History, RotateCcw } from "lucide-react";
import { FlowMap } from "@wa/widgets/flow-map";
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { LoadingState } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import {
  getFlowRevision,
  listFlowRevisions,
  type FlowOwner,
  type FlowRevisionSummary,
} from "../../../../lib/flowMap";
import { Btn, EmptyState, Modal, fmtDateTime, useToast } from "../../../ui";

export function FlowRevisionsModal({
  owner,
  ownerId,
  canEdit,
  currentVersion,
  onRestore,
  onClose,
}: {
  owner: FlowOwner;
  ownerId: string;
  /** The page's own permission check. Without it the list is read-only. */
  canEdit: boolean;
  /** The version on screen now, named at the top so the list reads as "before this". */
  currentVersion: number;
  /** Hands the chosen document to the panel, which saves it like any other edit. */
  onRestore: (doc: FlowMapDoc) => void;
  onClose: () => void;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [rows, setRows] = useState<FlowRevisionSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [preview, setPreview] = useState<FlowMapDoc | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    listFlowRevisions(owner, ownerId).then(
      (r) => alive && setRows(r.revisions),
      () => {
        if (!alive) return;
        setRows([]);
        setFailed(true);
      },
    );
    return () => {
      alive = false;
    };
  }, [owner, ownerId]);

  /** The version's document, from the server. A version pruned since the list was drawn is gone. */
  async function fetchDoc(version: number): Promise<FlowMapDoc | null> {
    try {
      return (await getFlowRevision(owner, ownerId, version)).doc;
    } catch {
      toast(t("flowMap.hist.gone"));
      return null;
    }
  }

  async function show(version: number) {
    if (open === version) {
      setOpen(null);
      setPreview(null);
      return;
    }
    setOpen(version);
    setPreview(null);
    const doc = await fetchDoc(version);
    // The reader may have moved on to another row while this was in flight.
    setOpen((cur) => {
      if (cur === version && doc) setPreview(doc);
      return cur;
    });
  }

  async function restore(version: number) {
    setBusy(true);
    const doc = preview && open === version ? preview : await fetchDoc(version);
    if (!doc) {
      setBusy(false);
      return;
    }
    onRestore(doc);
    toast(t("flowMap.hist.restored", { version }));
    onClose();
  }

  return (
    <Modal title={t("flowMap.hist.title")} onClose={onClose} className="max-w-[900px]">
      <div className="crm-m-body">
        <p className="crm-mut text-[12.5px]">{t("flowMap.hist.current", { version: currentVersion })}</p>

        {rows === null ? <LoadingState variant="inline" label={t("common.loading")} /> : null}
        {rows?.length === 0 ? (
          <EmptyState icon={History} title={failed ? t("flowMap.loadFailed") : t("flowMap.hist.none")} />
        ) : null}

        <ul className="flex flex-col gap-2.5">
          {(rows ?? []).map((rev) => (
            <li key={rev.id}>
              <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5 text-[12.5px]">
                <span className="font-semibold">v{rev.version}</span>
                <span className="crm-mut">{fmtDateTime(rev.at)}</span>
                <span className="crm-mut min-w-0 flex-1 truncate">{rev.savedByName ?? t("flowMap.hist.seed")}</span>
                <Btn small onClick={() => void show(rev.version)}>
                  {open === rev.version ? t("flowMap.hist.hide") : t("flowMap.hist.show")}
                </Btn>
                {canEdit ? (
                  <Btn small onClick={() => void restore(rev.version)} disabled={busy}>
                    <RotateCcw className="size-3.5" />
                    {t("flowMap.hist.restore")}
                  </Btn>
                ) : null}
              </div>
              {open === rev.version ? (
                <div className="mt-2.5 rounded-[18px] bg-[var(--crm-surface)] p-2">
                  {preview ? (
                    <FlowMap
                      value={preview}
                      readOnly
                      locale={locale}
                      height={360}
                      scrollZoom={false}
                      ariaLabel={t("flowMap.hist.previewLabel", { version: rev.version })}
                    />
                  ) : (
                    <LoadingState variant="inline" label={t("common.loading")} />
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
